
import React, { useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Svg, Path } from 'react-native-svg';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
  withSequence,
  withDelay,
  runOnJS,
} from 'react-native-reanimated';

interface ToastProps {
  visible: boolean;
  type: 'success' | 'error' | 'info';
  message: string;
  onHide: () => void;
  duration?: number;
}

export default function Toast({ visible, type, message, onHide, duration = 4000 }: ToastProps) {
  const translateY = useSharedValue(100);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (visible) {
      // Show animation
      translateY.value = withTiming(0, { duration: 300 });
      opacity.value = withTiming(1, { duration: 300 });

      // Auto hide after duration
      const timer = setTimeout(() => {
        hideToast();
      }, duration);

      return () => clearTimeout(timer);
    } else {
      hideToast();
    }
  }, [visible]);

  const hideToast = () => {
    translateY.value = withSequence(
      withTiming(0, { duration: 100 }),
      withTiming(100, { duration: 300 }, () => {
        runOnJS(onHide)();
      })
    );
    opacity.value = withTiming(0, { duration: 300 });
  };

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }));

  const getToastStyle = () => {
    switch (type) {
      case 'success':
        return styles.successToast;
      case 'error':
        return styles.errorToast;
      case 'info':
        return styles.infoToast;
      default:
        return styles.successToast;
    }
  };

  const getTextStyle = () => {
    switch (type) {
      case 'success':
        return styles.successText;
      case 'error':
        return styles.errorText;
      case 'info':
        return styles.infoText;
      default:
        return styles.successText;
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <Path d="M8.28216 3H16.7169C19.665 3 21.5 5.08119 21.5 8.02638V15.9736C21.5 18.9188 19.665 21 16.7159 21H8.28216C5.33405 21 3.5 18.9188 3.5 15.9736V8.02638C3.5 5.08119 5.34281 3 8.28216 3Z" stroke="#026C45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M9.03516 12.0003L11.345 14.3091L15.9627 9.69141" stroke="#026C45" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        );
      case 'error':
        return (
          <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <Path d="M8.28216 3H16.7169C19.665 3 21.5 5.08119 21.5 8.02638V15.9736C21.5 18.9188 19.665 21 16.7159 21H8.28216C5.33405 21 3.5 18.9188 3.5 15.9736V8.02638C3.5 5.08119 5.34281 3 8.28216 3Z" stroke="#A73C00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M14.8284 9.6601L10.166 14.3226M14.8343 14.3285L10.1641 9.6582" stroke="#A73C00" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        );
      case 'info':
        return (
          <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <Path d="M3 12C3 16.9699 7.02908 21 12 21C16.9709 21 21 16.9699 21 12C21 7.02908 16.9709 3 12 3C7.02908 3 3 7.02908 3 12Z" stroke="#6600CC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M12.0057 15.6932V11.3936M12 8.35426V8.29102" stroke="#6600CC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        );
      default:
        return null;
    }
  };

  const getCloseIconColor = () => {
    switch (type) {
      case 'success':
        return '#026C45';
      case 'error':
        return '#A73C00';
      case 'info':
        return '#6600CC';
      default:
        return '#026C45';
    }
  };

  if (!visible) return null;

  return (
    <View style={styles.container}>
      <Animated.View style={[styles.toast, getToastStyle(), animatedStyle]}>
        {getIcon()}
        <Text style={[styles.message, getTextStyle()]}>{message}</Text>
        <TouchableOpacity onPress={hideToast} style={styles.closeButton}>
          <Svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <Path d="M4.75781 13.2426L13.2431 4.75736" stroke={getCloseIconColor()} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <Path d="M4.75781 4.75735L13.2431 13.2426" stroke={getCloseIconColor()} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </Svg>
        </TouchableOpacity>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 24,
    left: 24,
    right: 24,
    zIndex: 1000,
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: {
      width: 4,
      height: 4,
    },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 4,
  },
  successToast: {
    backgroundColor: '#CFEEE3',
    borderColor: '#0FA971',
  },
  errorToast: {
    backgroundColor: '#FFD0B6',
    borderColor: '#DE5609',
  },
  infoToast: {
    backgroundColor: '#F2E4FF',
    borderColor: '#DDBAFF',
  },
  message: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    lineHeight: 19.6,
  },
  successText: {
    color: '#026C45',
  },
  errorText: {
    color: '#A73C00',
  },
  infoText: {
    color: '#6600CC',
  },
  closeButton: {
    justifyContent: 'center',
    alignItems: 'center',
    width: 24,
    height: 24,
    padding: 3,
  },
});
